import { toEnglishNumbers } from './numbers';

/** Keeps only characters that can participate in an Iranian mobile number. */
export function sanitizeIranianMobileInput(raw: unknown): string {
  const english = toEnglishNumbers(raw);
  const compact = english.replace(/[\s()\-._]/g, '');
  const hasLeadingPlus = compact.trimStart().startsWith('+');
  const digits = compact.replace(/\D/g, '');
  if (!digits) return hasLeadingPlus ? '+' : '';
  return `${hasLeadingPlus ? '+' : ''}${digits}`.slice(0, 14);
}

/** Canonicalizes common Iranian mobile formats without rejecting partial typing. */
export function normalizeIranianMobile(raw: unknown): string {
  const input = sanitizeIranianMobileInput(raw);
  if (!input) return '';
  if (/^00989\d{9}$/.test(input)) return `+${input.slice(2)}`;
  if (/^989\d{9}$/.test(input)) return `+${input}`;
  if (/^9\d{9}$/.test(input)) return `0${input}`;
  return input;
}

export function isValidIranianMobile(raw: unknown): boolean {
  const input = normalizeIranianMobile(raw);
  return /^(?:09\d{9}|\+989\d{9})$/.test(input);
}

/** Keeps only characters that can participate in an Iranian fixed-line number. */
export function sanitizeIranianLandlineInput(raw: unknown): string {
  const english = toEnglishNumbers(raw);
  const compact = english.replace(/[\s()\-._]/g, '');
  const hasLeadingPlus = compact.trimStart().startsWith('+');
  const digits = compact.replace(/\D/g, '');
  if (!digits) return hasLeadingPlus ? '+' : '';
  return `${hasLeadingPlus ? '+' : ''}${digits}`.slice(0, 14);
}

/** Canonicalizes local, national and international Iranian fixed-line formats. */
export function normalizeIranianLandline(raw: unknown): string {
  const input = sanitizeIranianLandlineInput(raw);
  if (!input) return '';
  if (/^0098[1-8]\d{9}$/.test(input)) return `+${input.slice(2)}`;
  if (/^98[1-8]\d{9}$/.test(input)) return `+${input}`;
  if (/^[1-8]\d{9}$/.test(input)) return `0${input}`;
  return input;
}

export function isValidIranianLandline(raw: unknown): boolean {
  const input = normalizeIranianLandline(raw);
  return /^(?:[1-9]\d{7}|0[1-8]\d{9}|\+98[1-8]\d{9})$/.test(input);
}

/** Adds https:// when an administrator/end user enters a bare host name. */
export function normalizeHttpUrl(raw: unknown): string {
  const value = String(raw ?? '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (/^\/\//.test(value)) return `https:${value}`;
  return `https://${value}`;
}

export function isValidHttpUrl(raw: unknown): boolean {
  const value = String(raw ?? '').trim();
  if (!value || /\s/.test(value)) return false;
  try {
    const parsed = new URL(normalizeHttpUrl(value));
    if (!['http:', 'https:'].includes(parsed.protocol)) return false;
    if (!parsed.hostname) return false;
    // Domains, localhost and IP addresses are all useful in DNN installations.
    return parsed.hostname === 'localhost'
      || parsed.hostname.includes('.')
      || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(parsed.hostname)
      || parsed.hostname.includes(':');
  } catch {
    return false;
  }
}
